import React, { useEffect, useRef, useState } from 'react';
import { Send, CheckCircle } from 'lucide-react';
import Container from './Container';
import Button from './Button';

const DEFAULT_CONTACT_FORM_ENDPOINT = 'https://us-central1-wizantix.cloudfunctions.net/contactForm';
const contactFormEndpoint =
  (import.meta.env.VITE_CONTACT_FORM_ENDPOINT as string | undefined) ?? DEFAULT_CONTACT_FORM_ENDPOINT;
const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;
const localTestsEnabled = import.meta.env.VITE_LOCAL_TESTS_ENABLED === 'true';

const isLocalHost = (): boolean => {
  const { hostname } = window.location;
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1';
};

interface TurnstileApi {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string;
      theme?: 'light' | 'dark' | 'auto';
      action?: string;
      callback?: (token: string) => void;
      'expired-callback'?: () => void;
      'error-callback'?: () => void;
    }
  ) => string;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

interface FormData {
  name: string;
  email: string;
  subject: string;
  message: string;
  company: string; // honeypot
}

const INITIAL_FORM: FormData = { name: '', email: '', subject: '', message: '', company: '' };

const Contact: React.FC = () => {
  const [form, setForm] = useState<FormData>(INITIAL_FORM);
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');

  const turnstileContainerRef = useRef<HTMLDivElement | null>(null);
  const turnstileWidgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (isSubmitted) {
      return;
    }

    if (!turnstileSiteKey || !turnstileContainerRef.current) {
      return;
    }

    let cancelled = false;

    const renderWidget = () => {
      if (cancelled || !turnstileContainerRef.current || !window.turnstile || turnstileWidgetIdRef.current) {
        return;
      }

      turnstileWidgetIdRef.current = window.turnstile.render(turnstileContainerRef.current, {
        sitekey: turnstileSiteKey,
        theme: 'auto',
        action: 'contact_form',
        callback: (token: string) => {
          setTurnstileToken(token);
          setSubmitError('');
        },
        'expired-callback': () => setTurnstileToken(''),
        'error-callback': () => {
          setTurnstileToken('');
          setSubmitError('Turnstile challenge failed. Please retry the challenge.');
        },
      });
    };

    const existingScript = document.querySelector<HTMLScriptElement>('script[data-turnstile-script="true"]');
    if (window.turnstile) {
      renderWidget();
    } else if (existingScript) {
      existingScript.addEventListener('load', renderWidget);
    } else {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.dataset.turnstileScript = 'true';
      script.addEventListener('load', renderWidget);
      document.head.appendChild(script);
    }

    return () => {
      cancelled = true;
      existingScript?.removeEventListener('load', renderWidget);
      if (window.turnstile && turnstileWidgetIdRef.current) {
        window.turnstile.remove(turnstileWidgetIdRef.current);
        turnstileWidgetIdRef.current = null;
      }
    };
  }, [isSubmitted]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isLocalHost() && !localTestsEnabled) {
      setSubmitError('Local form testing is disabled. Set VITE_LOCAL_TESTS_ENABLED=true in .env to enable it.');
      return;
    }

    if (!contactFormEndpoint) {
      setSubmitError('Contact form is not configured yet. Please try again later.');
      return;
    }

    const isLocalTestMode = isLocalHost() && localTestsEnabled;

    if (!turnstileSiteKey && !isLocalTestMode) {
      setSubmitError('Turnstile is not configured for this environment.');
      return;
    }

    if (turnstileSiteKey && !turnstileToken) {
      setSubmitError('Please complete the Turnstile challenge.');
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const response = await fetch(contactFormEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          title: form.subject,
          message: form.message,
          company: form.company,
          turnstileToken,
        }),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => null) as { message?: string } | null;
        throw new Error(errorBody?.message ?? 'Request failed');
      }

      setIsSubmitted(true);
      setForm(INITIAL_FORM);
      setTurnstileToken('');
      if (window.turnstile && turnstileWidgetIdRef.current) {
        window.turnstile.reset(turnstileWidgetIdRef.current);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : '';
      setSubmitError(message || 'Unable to send your message right now. Please try again later.');
      setTurnstileToken('');
      if (window.turnstile && turnstileWidgetIdRef.current) {
        window.turnstile.reset(turnstileWidgetIdRef.current);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass =
    'w-full rounded-md border border-border bg-background px-4 py-3 text-foreground placeholder-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors';

  return (
    <section id="contact" className="bg-background py-16 md:py-24">
      <Container>
        <div className="mx-auto max-w-2xl">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold sm:text-4xl">Get in Touch</h2>
            <p className="text-lg text-muted-foreground">
              Have a question or feedback? We'd love to hear from you.
            </p>
          </div>

          {isSubmitted ? (
            <div className="flex flex-col items-center gap-4 rounded-xl border border-border bg-card p-12 text-center">
              <CheckCircle className="h-14 w-14 text-primary" />
              <h3 className="text-xl font-semibold">Message Sent!</h3>
              <p className="text-muted-foreground">
                Thanks for reaching out. We'll get back to you as soon as possible.
              </p>
              <Button variant="outline" onClick={() => setIsSubmitted(false)} className="mt-2">
                Send Another Message
              </Button>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="space-y-5 rounded-xl border border-border bg-card p-8"
              aria-describedby={submitError ? 'contact-submit-error' : undefined}
            >
              {/* Honeypot — hidden from real users, catches bots */}
              <div className="hidden" aria-hidden="true">
                <label htmlFor="contact-company">Company</label>
                <input
                  id="contact-company"
                  name="company"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.company}
                  onChange={handleChange}
                />
              </div>

              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-name" className="mb-1.5 block text-sm font-medium text-foreground">
                    Name <span className="text-primary">*</span>
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    placeholder="Your name"
                    value={form.name}
                    onChange={handleChange}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label htmlFor="contact-email" className="mb-1.5 block text-sm font-medium text-foreground">
                    Email <span className="text-primary">*</span>
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    placeholder="you@example.com"
                    value={form.email}
                    onChange={handleChange}
                    className={inputClass}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="contact-subject" className="mb-1.5 block text-sm font-medium text-foreground">
                  Subject <span className="text-primary">*</span>
                </label>
                <select
                  id="contact-subject"
                  name="subject"
                  required
                  value={form.subject}
                  onChange={handleChange}
                  className={inputClass}
                >
                  <option value="" disabled>Select a subject…</option>
                  <option value="General Inquiry">General Inquiry</option>
                  <option value="Game">Game</option>
                  <option value="Platform Feedback">Platform Feedback</option>
                  <option value="Bug Report">Bug Report</option>
                  <option value="Partnership">Partnership</option>
                  <option value="Press">Press</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label htmlFor="contact-message" className="mb-1.5 block text-sm font-medium text-foreground">
                  Message <span className="text-primary">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  rows={5}
                  placeholder="Tell us how we can help…"
                  value={form.message}
                  onChange={handleChange}
                  className={`${inputClass} resize-y`}
                />
              </div>

              {turnstileSiteKey ? (
                <div className="rounded-lg border border-border bg-card p-3">
                  <div ref={turnstileContainerRef} />
                </div>
              ) : (
                <p className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-700 dark:text-amber-100">
                  Turnstile is not configured in this environment.
                </p>
              )}

              {submitError && (
                <p
                  id="contact-submit-error"
                  role="alert"
                  aria-live="assertive"
                  className="rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-700 dark:text-red-200"
                >
                  {submitError}
                </p>
              )}

              <Button
                type="submit"
                size="lg"
                fullWidth
                disabled={isSubmitting}
                className="mt-2"
              >
                {isSubmitting ? (
                  'Sending…'
                ) : (
                  <>
                    Send Message
                    <Send className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </form>
          )}
        </div>
      </Container>
    </section>
  );
};

export default Contact;
